import moment from 'moment'
import React, { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { EuiButton, EuiButtonIcon, EuiFieldText, EuiFlexGroup, EuiFlexItem, EuiFlyoutBody, EuiFlyoutFooter, EuiFlyoutHeader, EuiFormRow, EuiSpacer, EuiTextArea, EuiTitle } from '@elastic/eui'
import AcFlyout from 'components/AcFlyout'

import { IntervalUnit } from 'api/interfaces'
import { useGetAIPlanStubQuery, useEditAIPlanStubCampaignBuilderMutation } from 'api/rtkQueryApi/platform/aiPlansApi'

export interface EditAIPlanCampaignKeywordsFlyoutProps {
  campaignBuilderId: string
  onClose: () => void
}

const EditAIPlanCampaignKeywordsFlyout: React.FC<EditAIPlanCampaignKeywordsFlyoutProps> = ({ campaignBuilderId, onClose }) => {
  const { aiPlanId } = useParams<{ aiPlanId: string }>()
  const { data: stub } = useGetAIPlanStubQuery({ aiPlanId }, { skip: !aiPlanId })
  const didFireUpdate = useRef(false)

  const builder = stub?.campaignBuilders.find(b => b.id === campaignBuilderId)

  const [keywordDescription, setKeywordDescription] = useState(builder?.keywordDescription || '')
  const [keywords, setKeywords] = useState<string[]>(builder?.keywords || [])
  const [newKeyword, setNewKeyword] = useState('')

  const [editCampaignBuilder, { isSuccess, isLoading }] = useEditAIPlanStubCampaignBuilderMutation()

  useEffect(() => {
    if (didFireUpdate.current && isSuccess) {
      didFireUpdate.current = false
      onClose()
    }
  }, [didFireUpdate, isSuccess, onClose])

  if (!builder || !stub) return null

  const onAddKeyword = () => {
    if (newKeyword.trim() && !keywords.includes(newKeyword.trim())) {
      setKeywords([...keywords, newKeyword.trim()])
      setNewKeyword('')
    }
  }

  const onRemoveKeyword = (index: number) => {
    setKeywords(keywords.filter((_, i) => i !== index))
  }

  const onSave = () => {
    const request = {
      name: builder.name,
      shortSummary: builder.shortSummary,
      longSummary: builder.longSummary,
      audienceSummary: builder.audienceSummary,
      audienceDetails: builder.audienceDetails || '',
      geoSummary: builder.geoSummary,
      geoDetails: builder.geoDetails || '',
      seasonalitySummary: builder.seasonalitySummary,
      seasonalityDetails: builder.seasonalityDetails || '',
      campaignType: builder.campaignType as unknown as string,
      isOffAdCritter: builder.isOffAdCritter,
      offAdCritterName: builder.offAdCritterName,
      offAdCritterColor: builder.offAdCritterColor,
      intervalBudget: builder.intervalBudget,
      retargetSite: builder.retargetSite,
      isTotalBudget: builder.isTotalBudget,
      startDate: moment(builder.startDate).toISOString(),
      endDate: builder.endDate ? moment(builder.endDate).toISOString() : undefined,
      intervalUnit: (builder.intervalUnit || IntervalUnit.Month) as unknown as string,
      intervalFrequency: builder.intervalFrequency || 1,
      intervalDuration: builder.intervalDuration || 1,
      segmentTargets: builder.segmentTargets || [],
      segmentDescriptions: builder.segmentDescriptions || [],
      audienceSegmentLooksUp: builder.audienceSegmentLooksUp || [],
      targetingType: builder.targetingType as unknown as string | undefined,
      regions: builder.regions || [],
      dmas: builder.dmas || [],
      cities: builder.cities || [],
      zips: builder.zips || [],
      keywords,
      keywordDescription,
      includeInProjections: builder.includeInProjections || false,
      includeInGeoHeatmap: builder.includeInGeoHeatmap || false
    }

    editCampaignBuilder({
      aiPlanStubId: stub.id,
      campaignBuilderId,
      aiPlanId,
      request
    })
    didFireUpdate.current = true
  }

  return (
    <AcFlyout onClose={onClose} size='m'>
      <EuiFlyoutHeader>
        <EuiTitle size='m'>
          <h2>Edit Keywords</h2>
        </EuiTitle>
      </EuiFlyoutHeader>
      <EuiFlyoutBody>
        <EuiFormRow fullWidth label='Keyword Description'>
          <EuiTextArea fullWidth value={keywordDescription} onChange={e => setKeywordDescription(e.target.value)} rows={6} />
        </EuiFormRow>

        <EuiSpacer size='m' />

        <EuiFormRow fullWidth label='Keywords'>
          <div>
            {keywords.map((keyword, index) => (
              <div key={index} style={{ marginBottom: '8px' }}>
                <EuiFlexGroup gutterSize='s' alignItems='center' responsive={false}>
                  <EuiFlexItem grow={false}>
                    <span style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: '#343741', marginRight: '10px', marginTop: '2px', flexShrink: 0, display: 'inline-block' }} />
                  </EuiFlexItem>
                  <EuiFlexItem>
                    <span>{keyword}</span>
                  </EuiFlexItem>
                  <EuiFlexItem grow={false}>
                    <EuiButtonIcon iconType='trash' color='danger' onClick={() => onRemoveKeyword(index)} aria-label={`Remove ${keyword}`} />
                  </EuiFlexItem>
                </EuiFlexGroup>
              </div>
            ))}
            <EuiSpacer size='s' />
            <EuiFlexGroup gutterSize='s' alignItems='center' responsive={false}>
              <EuiFlexItem>
                <EuiFieldText fullWidth placeholder='Add keyword...' value={newKeyword} onChange={e => setNewKeyword(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') onAddKeyword() }} />
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <EuiButton size='s' onClick={onAddKeyword} isDisabled={!newKeyword.trim()}>Add</EuiButton>
              </EuiFlexItem>
            </EuiFlexGroup>
          </div>
        </EuiFormRow>
      </EuiFlyoutBody>
      <EuiFlyoutFooter>
        <EuiFlexGroup direction='row' alignItems='center'>
          <EuiFlexItem grow={false}>
            <EuiButton onClick={onClose}>Cancel</EuiButton>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiButton fill onClick={onSave} isLoading={isLoading}>Save</EuiButton>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlyoutFooter>
    </AcFlyout>
  )
}

export default EditAIPlanCampaignKeywordsFlyout
